import { z } from 'zod';
import { IdSchema, InstantSchema, MoneySchema } from '../common/index.js';

/**
 * Lignes de prestation détaillées (demande de Joel du 30/10/2026), ajout au contrat 1.0.0. Chaque ligne de devis ou de facture porte
 * sa désignation complète : code, intitulé, sections (en gras) et sous-lignes, comme les devis actuels du Cabinet MAAT. C'est une
 * COPIE figée du catalogue versionné de l'API au moment de l'émission ; une ligne libre porte le détail saisi (code null).
 * Côté portail, la désignation seule : aucun montant, pour qu'aucune somme réglée ne se déduise d'un total et d'un reste dû.
 */
export const PrestationCodeSchema = z.string().regex(/^[A-Z][A-Z0-9-]{1,48}$/);
const Texte = (max: number) => z.string().trim().min(1).max(max);
const Centimes = z.number().int().min(0).max(100_000_000);

export const DetailSectionSchema = z
  .object({ titre: Texte(200), lignes: z.array(Texte(300)).max(40) })
  .meta({ id: 'DetailSection', description: 'Section du détail d’une prestation (titre en gras) et ses sous-lignes.' });

export const DesignationSchema = z
  .object({
    code: PrestationCodeSchema.nullable(),
    intitule: Texte(300),
    detail: z.array(DetailSectionSchema).max(40),
    catalogueVersion: z.string().nullable(),
  })
  .meta({ id: 'Designation', description: 'Désignation complète d’une ligne, copie figée à l’émission ; code null pour une ligne libre.' });

export const PrestationSchema = z
  .object({ code: PrestationCodeSchema, intitule: Texte(300), detail: z.array(DetailSectionSchema).max(40) })
  .meta({ id: 'Prestation', description: 'Prestation du catalogue versionné ; aucun tarif (prix saisi ou règle existante).' });

export const PrestationsCatalogueSchema = z
  .object({ version: z.string().regex(/^\d+\.\d+\.\d+$/), status: z.enum(['a_valider', 'valide']), prestations: z.array(PrestationSchema) })
  .meta({ id: 'PrestationsCatalogue', description: 'Catalogue versionné des prestations du cabinet (équipe de bureau).' });

/** Saisie d'une section : le serveur retire les espaces, les sous-lignes vides et les sections sans titre. */
const SectionSaisieSchema = z.object({ titre: z.string().max(200), lignes: z.array(z.string().max(300)).max(40) });

export const QuoteLineCreateSchema = z
  .object({
    prestationCode: PrestationCodeSchema.optional(),
    intitule: z.string().max(300).optional(),
    detail: z.array(SectionSaisieSchema).max(40).optional(),
    amountCents: Centimes,
  })
  .refine((l) => (l.prestationCode === undefined) !== (l.intitule === undefined && l.detail === undefined), {
    message: 'Une ligne vient du catalogue (code) ou est libre (intitulé et détail), jamais les deux.',
    path: ['prestationCode'],
  })
  .refine((l) => l.prestationCode !== undefined || l.intitule !== undefined, { message: 'Intitulé exigé.', path: ['intitule'] })
  .meta({ id: 'QuoteLineCreate', description: 'Ligne de devis : prestation du catalogue, ou ligne libre ; prix saisi.' });

export const QuoteCreateSchema = z
  .object({ phaseScope: z.enum(['jusqu_indivision', 'partage']), lignes: z.array(QuoteLineCreateSchema).min(1).max(30) })
  .meta({ id: 'QuoteCreate', description: 'Créer un devis : ses lignes, dans l’ordre.' });

export const QuoteLineSchema = z
  .object({ id: IdSchema, position: z.number().int().min(1), designation: DesignationSchema, amount: MoneySchema })
  .meta({ id: 'QuoteLine' });

export const QuoteSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    phaseScope: z.enum(['jusqu_indivision', 'partage']),
    status: z.enum(['brouillon', 'envoye', 'signe', 'refuse']),
    total: MoneySchema,
    createdAt: InstantSchema,
    lines: z.array(QuoteLineSchema),
  })
  .meta({ id: 'Quote', description: 'Devis du dossier et ses lignes détaillées (copie figée).' });

export const InvoiceLineSchema = z
  .object({ label: z.string().min(1), amount: MoneySchema, designation: DesignationSchema.nullable() })
  .meta({ id: 'InvoiceLine', description: 'Ligne d’une facture émise ; désignation figée si elle vient d’une prestation.' });

export const PortalPrestationsSchema = z
  .strictObject({
    devis: z.array(z.strictObject({ id: IdSchema, status: z.enum(['envoye', 'signe']), lines: z.array(DesignationSchema) })),
    factures: z.array(z.strictObject({ number: z.string().min(1), issuedAt: InstantSchema, lines: z.array(DesignationSchema) })),
  })
  .meta({ id: 'PortalPrestations', description: 'Ce que couvrent mes devis et mes factures : désignations seules, aucun montant.' });
